import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import axios from 'axios';

const RegistrationForm = () => {

    const location = useLocation();
    const { event, userId } = location.state || {};

    // State to store input values
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        rigInfo: '',
        truckerCamperTrailer: '',
        travelCompanions: '',
        emergencyContact: '',
        emergencyContactPhone: '',
        medicalConditions: '',
        initials: false,
        question1: '',
        question2: '',
        question3: '',
    });

    // State to store event questions
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    const fetchEventQuestions = useCallback(async () => {
        setLoading(true);

        try {
            const response = await axios.get(`/otgservices/getEventQuestions.php?event_id=${event.id}`);

            if (response.data.success && response.data.questions.length > 0) {
                // Sort questions by ordinal
                const sortedQuestions = response.data.questions
                    .sort((a, b) => a.ordinal - b.ordinal)
                    .map(q => ({ question: q.question, ordinal: q.ordinal }));

                setQuestions(sortedQuestions);
                toast.success('Questions loaded successfully.');

            } else {
                // No questions found, start with one empty question
                setQuestions([]);
                toast.info('No existing questions found for this event.');
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
            // Start with one empty question
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    }, [event.id,setQuestions, setLoading]);

    // Use the function in useEffect with proper dependencies
    useEffect(() => {
        if (event.id) {
            fetchEventQuestions();
        } else {
            setLoading(false);
        }
    }, [event.id, fetchEventQuestions]);

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    // Handle phone number changes
    const handlePhoneChange = (value) => {
        setFormData({
            ...formData,
            phoneNumber: value || '',
        });
    };

    // Handle emergency contact phone number changes
    const handleEmergencyPhoneChange = (value) => {
        setFormData({
            ...formData,
            emergencyContactPhone: value || '',
        });
    };

    // Handle checkbox change (for initials)
    const handleCheckboxChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.checked,
        });
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // userId passed via state and event also has current user id. should match...
        if ( userId !== event.currentuserid) {
            console.log("Mis matched Users Ids");
        }

        // Validate primary phone number
        const isPhoneValid = formData.phoneNumber ? isValidPhoneNumber(formData.phoneNumber) : false;

        // Validate emergency contact phone if provided
        const isEmergencyPhoneValid = !formData.emergencyContactPhone ||
            (formData.emergencyContactPhone && isValidPhoneNumber(formData.emergencyContactPhone));

        if (!formData.firstName || !formData.lastName || !formData.phoneNumber) {
            toast.error("First name, Last name, and phone number are required.");
            return;
        }
        if (!formData.rigInfo || !formData.truckerCamperTrailer || !formData.travelCompanions) {
            toast.error("Rig, Camper and Companions need are required.");
            return;
        }

        if (!formData.emergencyContact || !formData.emergencyContactPhone || !formData.medicalConditions) {
            toast.error("Emergency Contact, Emergency Contact Phone and Medical Conditions are required.");
            return;
        }

        if (!isPhoneValid) {
            toast.error("Please enter a valid phone number.");
            return;
        }

        if (!isEmergencyPhoneValid) {
            toast.error("Please enter a valid emergency contact phone number.");
            return;
        }

        if (!formData.initials) {
            toast.error("You must acknowledge the Terms of Use.");
            return;
        }

        if ( questions.length > 0 ) {
            if ( questions.length === 3 ) {
                if ( !formData.question1 || !formData.question2 || !formData.question3)  {
                    toast.error("All event specific questions must be answered.");
                    return;
                }
            }

            if ( questions.length === 2 ) {
                if ( !formData.question1 || !formData.question2 )  {
                    toast.error("All event specific questions must be answered.");
                    return;
                }
            }

            if ( questions.length === 1 ) {
                if ( !formData.question1 )  {
                    toast.error("The event specific question must be answered.");
                    return;
                }
            }
        }

        // Create FormData object for submission
        const formDataToSubmit = new FormData();
        formDataToSubmit.append('user_id', event.currentuserid);
        formDataToSubmit.append('id', event.id);
        formDataToSubmit.append('first_name', formData.firstName);
        formDataToSubmit.append('last_name', formData.lastName);
        formDataToSubmit.append('phone_number', formData.phoneNumber);
        formDataToSubmit.append('rig_make_model', formData.rigInfo || '');
        formDataToSubmit.append('truck_camper_description', formData.truckerCamperTrailer || '');
        formDataToSubmit.append('travel_companions', formData.travelCompanions || '');
        formDataToSubmit.append('emergency_contact_name', formData.emergencyContact || '');
        formDataToSubmit.append('emergency_contact_phone', formData.emergencyContactPhone || '');
        formDataToSubmit.append('medical_conditions', formData.medicalConditions || '');
        formDataToSubmit.append('initials', formData.initials ? 'true' : 'false');
        formDataToSubmit.append('question_1', formData.question1 || '');
        formDataToSubmit.append('question_2', formData.question2 || '');
        formDataToSubmit.append('question_3', formData.question3 || '');

        try {
            const response = await fetch(`/otgservices/joinEvent.php`, {
                method: 'POST',
                body: formDataToSubmit,
            });

            const data = await response.json();
            let res = data.result;
            let op = data.operation;

            if (res) {
                if (op) {
                    toast.success(`You joined the event successfully!`);
                } else {
                    toast.info(`You have been waitlisted.`);
                    alert('You have been waitlisted, you will get an email if your status changes');
                }

                setTimeout(() => navigate("/events"), 2000);
            } else {
                toast.error(`Failed to register for the event: ${data.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Fetch Error:', error);
            toast.error('An unexpected error occurred.');
        }
    };

    // Render a specific question field
    const renderQuestionField = (index) => {
        const question = questions[index - 1];
        const questionField = (
            <div key={`question-${index}`}>
                <label htmlFor={`question${index}`} className="block mb-1 text-sm font-medium">
                    {question ? question.question : `Question ${index}`}
                </label>
                <input
                    type="text"
                    id={`question${index}`}
                    name={`question${index}`}
                    value={formData[`question${index}`]}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    placeholder={`Answer ${question ? 'this question' : `Question ${index}`}`}
                />
            </div>
        );

        return questionField;
    };

    return (
        <>
            <ToastContainer position="top-right" reverseOrder={false}/>
            <div className="p-6 bg-gray-100 min-h-screen">
                <div className="max-w-[1200px] w-full mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-semibold mb-4">Registration Form (all fields required)</h2>
                    {loading ? (
                        <div className="bg-white p-6 rounded shadow flex justify-center items-center h-40">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        </div>
                    ) : (
                        <div className="bg-white p-6 rounded shadow space-y-4">
                            <div>
                                <label htmlFor="firstName" className="block mb-1 text-sm font-medium">First Name</label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                    placeholder="Enter your first name"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="lastName" className="block mb-1 text-sm font-medium">Last Name</label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                    placeholder="Enter your last name"
                                    required
                                />
                            </div>

                            {/* PhoneInput for primary phone number */}
                            <div>
                                <label htmlFor="phoneNumber" className="block mb-1 text-sm font-medium">Phone
                                    Number</label>
                                <PhoneInput
                                    international
                                    defaultCountry="US"
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handlePhoneChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                    placeholder="Enter your phone number"
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Format: +[country code] [phone number]
                                </p>
                            </div>

                            <div>
                                <label htmlFor="rigInfo" className="block mb-1 text-sm font-medium">
                                    Rig (Make, Model, any modifications)
                                </label>
                                <input
                                    type="text"
                                    id="rigInfo"
                                    name="rigInfo"
                                    value={formData.rigInfo}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                    placeholder="Enter rig information"
                                />
                            </div>

                            <div>
                                <label htmlFor="truckerCamperTrailer" className="block mb-1 text-sm font-medium">
                                    Do you have a trucker camper or trailer? (if yes, please describe)
                                </label>
                                <input
                                    type="text"
                                    id="truckerCamperTrailer"
                                    name="truckerCamperTrailer"
                                    value={formData.truckerCamperTrailer}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                    placeholder="Enter details about trucker, camper, or trailer"
                                />
                            </div>

                            <div>
                                <label htmlFor="travelCompanions" className="block mb-1 text-sm font-medium">
                                    Who will be traveling with you?
                                </label>
                                <input
                                    type="text"
                                    id="travelCompanions"
                                    name="travelCompanions"
                                    value={formData.travelCompanions}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                    placeholder="Enter names of travel companions"
                                />
                            </div>

                            <div>
                                <label htmlFor="emergencyContact" className="block mb-1 text-sm font-medium">
                                    Emergency Contact Name & Relation
                                </label>
                                <input
                                    type="text"
                                    id="emergencyContact"
                                    name="emergencyContact"
                                    value={formData.emergencyContact}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                    placeholder="Name and relation (e.g., John Smith, Brother)"
                                />
                            </div>

                            {/* PhoneInput for emergency contact phone */}
                            <div>
                                <label htmlFor="emergencyContactPhone" className="block mb-1 text-sm font-medium">
                                    Emergency Contact Phone Number
                                </label>
                                <PhoneInput
                                    international
                                    defaultCountry="US"
                                    id="emergencyContactPhone"
                                    name="emergencyContactPhone"
                                    value={formData.emergencyContactPhone}
                                    onChange={handleEmergencyPhoneChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                    placeholder="Enter emergency contact phone number"
                                />
                            </div>

                            <div>
                                <label htmlFor="medicalConditions" className="block mb-1 text-sm font-medium">
                                    Please list any medical conditions or other info you'd like to share
                                </label>
                                <textarea
                                    id="medicalConditions"
                                    name="medicalConditions"
                                    value={formData.medicalConditions}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                    placeholder="Enter any medical conditions or relevant info"
                                />
                            </div>

                            {/* Event-specific Questions */}
                            {questions.length > 0 && (
                                <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
                                    <h3 className="font-medium text-lg mb-3">Event-Specific Questions</h3>
                                    <div className="space-y-4">
                                        {questions.map((q, idx) => renderQuestionField(idx + 1))}
                                    </div>
                                </div>
                            )}


                            <div>
                                <label htmlFor="initials" className="block mb-1 text-sm font-medium">
                                    <a
                                    href="https://www.overlandtrailguides.com/terms-of-use"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600">Overland Trail Guides Terms of Use</a>.
                                </label>
                                <input
                                    type="checkbox"
                                    id="initials"
                                    name="initials"
                                    checked={formData.initials}
                                    onChange={handleCheckboxChange}
                                    className="mr-2"
                                    required
                                />
                                <span>By checking this box I agree to the Terms of Use</span>
                            </div>

                            <div className="flex justify-between items-center mt-6">
                                <button
                                    type="button"
                                    onClick={() => navigate('/events/#event-' + event.id)}
                                    className="bg-gray-300 text-gray-700 px-2 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base rounded hover:bg-gray-400"
                                >
                                    Back to Events
                                </button>

                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    className="bg-blue-500 text-white px-2 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base rounded hover:bg-blue-600"
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default RegistrationForm;